import {
  Feather,
  FontAwesome,
  FontAwesome5,
  MaterialIcons,
} from "@expo/vector-icons";
import React, { useState, useEffect, useMemo } from "react";
import { Dimensions, View, Alert } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Centered,
  ErrorMessage,
  Input,
  Row,
  StepHeader,
} from "../../../components/all";
import { getStatesByCountry } from "../../../constants/statesByCountry";
import { COUNTRIES, getAdministrativeDivisionLabel } from "../../../constants/countries";
import { addFields, resetEmptyFields } from "../../../reducers/auth/register";
import { resetEmptyFields as resetProfileEmptyFields } from "../../../reducers/profile/profile";
import Styles from "../../../constants/styles";
import { useNavigation } from "@react-navigation/native";
import { useQuery } from "@tanstack/react-query";
import { getCoord } from "../../../services/shared";
import { useCitiesByCountry } from "../../../hooks/useCitiesByCountry";

export default function RegisterLocation({ route }) {
  const { jobType } = route?.params || {};
  const setField = (field, value) =>
    setAllFields({ ...allFields, [field]: value });
  const navigation = useNavigation();

  const [focusedInput, setFocusedInput] = useState(null);
  const [allFields, setAllFields] = useState({});
  const [isGeocodingLoading, setIsGeocodingLoading] = useState(false);

  const editProfile = false;

  const dispatch = useDispatch();
  const { isDoctor, country: userCountry } = useSelector((state) => state.user);
  const { fields, errorFields } = useSelector(
    (state) => state[editProfile ? "profile" : "register"]
  );

  // Get the appropriate administrative division label for the country
  const divisionLabel = getAdministrativeDivisionLabel(userCountry);

  // Fetch dynamic states/provinces data
  const {
    data: citiesData,
    isLoading: citiesLoading,
    isError: citiesError,
    isUsingFallback
  } = useCitiesByCountry(userCountry);

  // Transform states data for picker format
  const statesPickerItems = useMemo(() => {
    if (!citiesData || citiesData.length === 0) {
      // Fallback to static data if no dynamic data available
      const staticStates = getStatesByCountry(userCountry);
      // Sort static states alphabetically
      return staticStates.sort((a, b) =>
        (a.label || a.value).localeCompare(b.label || b.value)
      );
    }

    // Transform API data to picker format and sort alphabetically
    const transformedStates = citiesData.map(state => ({
      label: state.name || state.value,
      value: state.value || state.name,
      coord: state.coord
    }));

    // Sort by label alphabetically
    return transformedStates.sort((a, b) => a.label.localeCompare(b.label));
  }, [citiesData, userCountry]);

  const setErrorFields = () => {
    dispatch(editProfile ? resetProfileEmptyFields() : resetEmptyFields());
  };
  const validate = () => {
    setErrorFields();
    let error = false;
    dispatch(
      addFields({
        ...fields,
        ...allFields,
      })
    );
    setIsGeocodingLoading(true);
    locationRefetch();
  };
  const {
    data: locationQuery,
    refetch: locationRefetch,
    isError: isLocationError,
    error: locationError,
    isLoading: isLocationLoading
  } = useQuery(
    "clinicLocation",
    () => {
      const countryName = COUNTRIES[userCountry]?.name || "Malaysia";
      return getCoord(
        `${allFields.address}, ${allFields.city}, ${allFields.postcode} ${allFields.state}, ${countryName}`
      );
    },
    {
      enabled: false,
      onError: (error) => {
        setIsGeocodingLoading(false);
        const errorMessage = error?.response?.data?.message || "Unable to locate the address. Please check your address details and try again.";
        Alert.alert(
          "Address Not Found",
          errorMessage,
          [
            {
              text: "OK",
              onPress: () => console.log("Geocoding error acknowledged"),
            },
          ]
        );
      },
      onSuccess: (data) => {
        setIsGeocodingLoading(false);
      }
    }
  );

  useEffect(() => {
    console.log("fields", fields);
    if (locationQuery?.data && !isLocationError) {
      const countryName = COUNTRIES[userCountry]?.name || "Malaysia";
      navigation.navigate("ClinicLocatorScreen", {
        coords: locationQuery?.data.data,
        address: `${allFields.address}, ${allFields.city}, ${allFields.postcode} ${allFields.state}, ${countryName}`,
        jobType: jobType,
      });
    }
  }, [locationQuery, isLocationError]);

  const input = {
    address: "address",
    city: "city",
    postcode: "postcode",
    state: "state",
  };

  useEffect(() => {
    if (allFields?.clinic_profile_image) {
      setImageUrl(allFields?.clinic_profile_image);
    }
  }, [allFields?.clinic_profile_image]);

  return (
    <View
      style={{
        ...Styles.webSmallContainer,
        marginTop: 100,
      }}
    >
      {editProfile && isDoctor ? (
        <View style={{ marginTop: -45 }} />
      ) : (
        <StepHeader
          label={isDoctor ? `Contact \nInformation` : `Facility \nInformation`}
          icon={
            isDoctor ? (
              <Feather name="smartphone" size={24} color="black" />
            ) : (
              <FontAwesome5 name="clinic-medical" size={24} color="black" />
            )
          }
          subtitle={
            isDoctor ? `How can we reach you?` : `Describe your clinic detail.`
          }
        />
      )}
      <Centered
        style={{ width: Dimensions.get("window").width > 480 ? 480 : "100%" }}
      >
        <Input
          onChangeText={(val) => {
            setField(input.address, val);
          }}
          value={allFields[input.address]}
          icon={<FontAwesome name="home" size={14} color="grey" />}
          placeholder={isDoctor ? "Home Address" : "Facility Address"}
          containerStyle={{ width: "90%", marginTop: 10 }}
          onBlur={() => setFocusedInput(null)}
          onFocus={() => {
            setErrorFields();
            setFocusedInput(input.address);
            setIsGeocodingLoading(false);
          }}
          focused={focusedInput === input.address}
          error={errorFields.includes(input.address)}
          autoCapitalize="words"
        />
        <Input
          onChangeText={(val) => {
            setField(input.city, val);
          }}
          value={allFields[input.city]}
          icon={<MaterialIcons name="location-city" size={14} color="grey" />}
          placeholder="City/Town"
          containerStyle={{ width: "90%", marginTop: 10 }}
          onBlur={() => setFocusedInput(null)}
          onFocus={() => {
            setErrorFields();
            setFocusedInput(input.city);
            setIsGeocodingLoading(false);
          }}
          error={errorFields.includes(input.city)}
          focused={focusedInput === input.city}
          autoCapitalize="words"
        />
        <Row>
          <Input
            onChangeText={(val) => {
              setField(input.postcode, val);
            }}
            value={allFields[input.postcode]}
            icon={<FontAwesome name="location-arrow" size={14} color="grey" />}
            placeholder="Postal Code"
            containerStyle={{
              width:
                Dimensions.get("window").width > 480
                  ? 210
                  : Dimensions.get("window").width / 2.28,
              marginTop: 10,
              marginRight: 10,
            }}
            keyboardType="numeric"
            onBlur={() => setFocusedInput(null)}
            // maxLength={5}
            onFocus={() => {
              setErrorFields();
              setFocusedInput(input.postcode);
              setIsGeocodingLoading(false);
            }}
            error={
              errorFields.includes(input.postcode) ||
              errorFields.includes("postcodeError")
            }
            focused={focusedInput === input.postcode}
            inputStyle={{ width: "50%" }}
          />
          <Input
            isPicker
            onChangeText={(val) => {
              setField(input.state, val);
            }}
            value={allFields[input.state]}
            placeholder={divisionLabel}
            containerStyle={{
              width:
                Dimensions.get("window").width > 480
                  ? 210
                  : Dimensions.get("window").width / 2.28,
              marginTop: 10,
            }}
            onBlur={() => setFocusedInput(null)}
            onFocus={() => {
              setErrorFields();
              setFocusedInput(input.state);
              setIsGeocodingLoading(false);
            }}
            error={errorFields.includes(input.state)}
            focused={focusedInput === input.state}
            pickerItems={statesPickerItems}
            loading={citiesLoading}
          />
        </Row>
      </Centered>
      <View style={{ margin: 10, marginLeft: 24, marginBottom: 20 }}>
        <ErrorMessage
          visible={
            errorFields.includes(input.phone_number) ||
            errorFields.includes(input.emergency_number) ||
            errorFields.includes(input.address) ||
            errorFields.includes(input.city) ||
            errorFields.includes(input.postcode) ||
            errorFields.includes(input.state)
          }
          label="Please fill in the required fields."
        />
        <ErrorMessage
          visible={errorFields.includes("phoneError")}
          label="Please enter a valid Mobile number."
        />
        <ErrorMessage
          visible={errorFields.includes("postcodeError")}
          label="Please enter a valid postcode."
        />
        <ErrorMessage
          visible={errorFields.includes("business_registration_numberError")}
          label={"Please enter a valid Business Registration Number."}
        />
      </View>
      <Centered>
        <Row>
          <Button
            label="Back"
            type={"secondary"}
            onPress={() => {
              navigation.goBack();
            }}
          />
          <Button
            label={isGeocodingLoading ? "Locating..." : "Submit"}
            type={"primary"}
            onPress={validate}
            disabled={isGeocodingLoading}
          />
        </Row>
      </Centered>
    </View>
  );
}
